// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { ArrowSortUpRegular, ArrowSortDownRegular } from '@fluentui/react-icons';
import type { SortDirection } from '../hooks/useSortableTable';

interface SortableHeaderProps<TField extends string = string> {
  /** The column key this header represents */
  column: TField;
  /** Currently active sort column (from useSortableTable) */
  activeColumn: TField;
  /** Current sort direction (from useSortableTable) */
  direction: SortDirection;
  /** Click handler (from useSortableTable) */
  onSort: (column: TField) => void;
  /** Header label content */
  children: React.ReactNode;
  /** Optional additional className for the <th> */
  className?: string;
  /** Optional inline style (e.g. width overrides) */
  style?: React.CSSProperties;
}

/**
 * Shared sortable table header component.
 *
 * Renders a <th> with click-to-sort behaviour and a Fluent UI sort direction icon
 * on the active column. Inactive columns show no icon.
 *
 * @example
 * <SortableHeader column="control" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort}>
 *   {intl.formatMessage({ id: 'col.control' })}
 * </SortableHeader>
 */
export function SortableHeader<TField extends string = string>({
  column,
  activeColumn,
  direction,
  onSort,
  children,
  className,
  style,
}: SortableHeaderProps<TField>) {
  const isActive = column === activeColumn;

  return (
    <th
      className={`sortable-header${className ? ` ${className}` : ''}`}
      onClick={() => { onSort(column); }}
      style={style}
    >
      {children}
      {isActive && (
        <span className="sort-icon">
          {direction === 'asc' ? <ArrowSortUpRegular /> : <ArrowSortDownRegular />}
        </span>
      )}
    </th>
  );
}
